import {
  useEffect,
  createContext,
  useContext,
  ReactNode,
  useState,
  useMemo,
} from 'react';

import Suspender from 'react-suspender';
import {
  ApolloQueryResult,
  OperationVariables,
  useLazyQuery,
} from '@apollo/client';

import { useAuth } from './Auth';

import { LIST_STRATEGIES_BY_PROFILE } from '../http/apollo-queries';

import { IStrategy } from '../types/model';
import { logger } from '../helpers/logger';

interface IUserStrategiesContext {
  allStrategies: IStrategy[];
  canAddStrategy: boolean;
  draftStrategy?: IStrategy;
  draftCloneStrategy?: IStrategy;
  loading: boolean;
  lastFetchDate: Date;
  strategies: IStrategy[];

  refetch(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<ApolloQueryResult<any>>;
}

interface Props {
  children?: ReactNode;
}

const refetch = (): any => {};

const actions = {
  refetch,
};

const UserStrategiesContext = createContext<IUserStrategiesContext>({
  allStrategies: [],
  canAddStrategy: false,
  draftStrategy: undefined,
  draftCloneStrategy: undefined,
  loading: true,
  lastFetchDate: new Date(),
  strategies: [],
  ...actions,
});

export const useUserStrategies = () => useContext(UserStrategiesContext);

export const UserStrategiesProvider = ({ children }: Props) => {
  const { user } = useAuth();
  let lastFetchDate = new Date();
  const [loading, setLoading] = useState(true);

  const [
    getStrategies,
    {
      data: {
        listStrategiesByProfile: { items: allStrategies = [] } = {},
      } = {},
      loading: loadingStrategies,
      refetch: refetchStrategies,
    },
  ] = useLazyQuery<{ listStrategiesByProfile: { items: IStrategy[] } }>(
    LIST_STRATEGIES_BY_PROFILE,
    {
      variables: { profileId: user?.profile?.id },
      fetchPolicy: 'cache-and-network',
      pollInterval: 60000,
    }
  );

  const maxStrategies =
    user?.membership?.membershipPlan?.features?.strategies || 0;

  const { canAddStrategy, draftStrategy, draftCloneStrategy, strategies } =
    useMemo(() => {
      const strategies = allStrategies.filter(
        ({ status }) => status === 'COMPLETED'
      );

      const canAddStrategy =
        maxStrategies === -1 || maxStrategies > strategies.length;

      const draftStrategy = allStrategies.find(
        ({ status, flow }) => status !== 'COMPLETED' && flow === 'SCRATCH'
      );

      const draftCloneStrategy = allStrategies.find(
        ({ status, flow }) => status !== 'COMPLETED' && flow === 'CLONE'
      );

      logger.log('🟢 UserStrategiesContext:StrategiesMemo', {
        allStrategies,
        strategies,
        draftStrategy,
        draftCloneStrategy,
      });

      return { strategies, canAddStrategy, draftStrategy, draftCloneStrategy };
    }, [allStrategies, maxStrategies]);

  const refetch = (): Promise<ApolloQueryResult<any>> => {
    lastFetchDate = new Date();
    return refetchStrategies();
  };

  useEffect(() => {
    if (user?.profile?.id) {
      getStrategies()
        .catch((err) => {
          logger.error('🔴 UserStrategiesContext:Strategies', err);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [user?.profile?.id]);

  const value = {
    allStrategies,
    canAddStrategy,
    draftStrategy,
    draftCloneStrategy,
    strategies,
    loading: loadingStrategies,
    ...actions,
    refetch,
    lastFetchDate,
  };

  return (
    <UserStrategiesContext.Provider value={value}>
      <Suspender suspend={loading}>{children}</Suspender>
    </UserStrategiesContext.Provider>
  );
};
